/**
 * Base chart component with consistent theming and loading states
 */

import React from 'react';
import { ChartOptions, ChartData, ChartType } from 'chart.js';
import { getDefaultOptions, getAnimationConfig, ChartTheme, lightTheme } from '../../utils/chartConfig';

interface BaseChartProps<T extends ChartType = ChartType> {
  data: ChartData<T>;
  options?: ChartOptions<T>;
  theme: ChartTheme;
  loading?: boolean;
  error?: string | null;
  height?: number;
  className?: string;
  children: (props: {
    data: ChartData<T>;
    options: ChartOptions<T>;
    height: number;
  }) => React.ReactElement;
}

const BaseChart = <T extends ChartType = ChartType>({
  data,
  options = {} as ChartOptions<T>,
  theme,
  loading = false,
  error = null,
  height = 300,
  className = '',
  children
}: BaseChartProps<T>) => {
  const defaultOptions = getDefaultOptions(theme, 'line');
  const animationConfig = getAnimationConfig();
  
  // Determine if we're in light or dark mode based on theme background
  const isLightMode = theme.background === lightTheme.background;
  
  const mergedOptions: ChartOptions<T> = {
    ...defaultOptions,
    ...options,
    animation: {
      ...animationConfig,
      ...(options?.animation || {})
    }
  } as ChartOptions<T>;

  if (loading) {
    return (
      <div 
        className={`flex items-center justify-center rounded-lg border p-4 ${className} ${
          isLightMode 
            ? 'bg-white border-gray-200' 
            : 'bg-gray-800 border-gray-700'
        }`}
        style={{ height: `${height}px` }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33FF00] mx-auto mb-2"></div>
          <p className={`text-sm ${isLightMode ? 'text-gray-500' : 'text-gray-400'}`}>Loading chart...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className={`flex items-center justify-center rounded-lg border p-4 ${className} ${
          isLightMode 
            ? 'bg-white border-gray-200' 
            : 'bg-gray-800 border-gray-700'
        }`}
        style={{ height: `${height}px` }}
      >
        <div className="text-center text-red-500">
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`rounded-lg border p-4 ${className} ${
        isLightMode 
          ? 'bg-white border-gray-200' 
          : 'bg-gray-800 border-gray-700'
      }`}
      style={{ height: `${height}px` }}
    >
      {children({ data, options: mergedOptions, height })}
    </div>
  );
};

export default BaseChart;
